<template>
  <div>
    <p class="text-[14px] leading-[22px] text-gray-800">
      Preview of potential infringement matches from report
    </p>

    <div class="mt-3.5 rounded-xl bg-[#f9f9fa] px-3 py-2.5">
      <div class="flex items-start gap-x-2.5">
        <div class="min-w-0 flex-1">
          <div class="text-[12px] leading-4 text-gray-500">
            Your registered work
          </div>
          <div class="mt-1 flex items-center gap-x-2.5">
            <div class="size-[30px] shrink-0">
              <Thumbnail :type="registered.type" :src="registered.src" />
            </div>
            <span class="truncate text-[14px] leading-[18px] text-gray-800">
              {{ registered.name }}
            </span>
          </div>
          <button
            type="button"
            class="text-link mt-2 block text-[14px] leading-[18px]"
            @click="handleViewRegistered"
          >
            View
          </button>
        </div>

        <SortIcon
          class="mt-[26px] size-[18px] shrink-0 rotate-90 text-gray-400"
        />

        <div class="min-w-0 flex-1">
          <div class="text-[12px] leading-4 text-gray-500">
            Potential infringing work
          </div>
          <div class="mt-1 flex items-center gap-x-2.5">
            <div class="size-[30px] shrink-0">
              <Thumbnail :type="infringing.type" :src="infringing.src" />
            </div>
            <span class="truncate text-[14px] leading-[18px] text-gray-800">
              {{ infringing.name }}
            </span>
          </div>
          <button
            v-if="infringingFile.copyrightId"
            type="button"
            class="text-link mt-2 block text-[14px] leading-[18px]"
            @click="handleViewInfringing"
          >
            View
          </button>
        </div>
      </div>
    </div>

    <div class="mt-3.5 overflow-hidden rounded-xl border" :class="level.border">
      <div
        class="flex items-center justify-between gap-x-3 px-4 py-2.5 text-white"
        :class="level.banner"
      >
        <div class="max-w-[120px] text-[12px] leading-[15px] font-medium">
          Overall potential infringement match
        </div>
        <span class="shrink-0 text-[20px] leading-[26px] font-medium -mt-px">
          {{ level.label }}
        </span>
      </div>

      <div class="bg-white px-4 pt-4 pb-4">
        <p class="text-[14px] leading-[20px] text-gray-800">{{ level.body }}</p>
        <p class="mt-3.5 text-center text-[14px] leading-[20px] text-gray-600">
          Get the report to see full findings
        </p>
      </div>
    </div>

    <p class="mt-3.5 text-[12px] leading-[18px] font-medium text-gray-600">
      Creating this report uses 1 report credit. Once created, the credit is
      used and isn't returned to your balance.
    </p>

    <p class="mt-2.5 text-[12px] leading-[18px] text-gray-600">
      By selecting Create report, I confirm I'm acting in good faith and that I
      hold the rights to the original work. I understand Dacr will store the
      original and detected material to process this report, and that I'm
      responsible for the report I create. I agree to the
      <a class="text-link" href="/legal/terms" target="_blank">Dacr Terms</a>,
      <a class="text-link" href="/legal/privacy" target="_blank"
        >Privacy Policy</a
      >, and
      <a class="text-link" href="/legal/payments" target="_blank"
        >Payments Policy</a
      >.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Thumbnail } from '@dacr/library'
import { SortIcon } from '@dacr/icons'

import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { closeDialog } from '@/shared/dialogs/dialog'
import type { FlaggedInfringement } from './useFlaggedMatch'

const props = defineProps<{ infringement: FlaggedInfringement }>()

const router = useRouter()

const LEVELS = {
  [ScoreLevel.Low]: {
    label: 'Minimal',
    banner: 'bg-gray-300',
    border: 'border-gray-300',
    body: 'The compared work demonstrates a low overall similarity to your registered work. However, a low overall rating does not necessarily exclude the presence of isolated sections or elements with stronger similarities. A full technical report may provide additional insight into specific matches, detected patterns, and the relevance of individual similarities identified during analysis.',
  },
  [ScoreLevel.Medium]: {
    label: 'Moderate',
    banner: 'bg-yellow-500',
    border: 'border-yellow-500',
    body: 'The compared work demonstrates a moderate overall similarity to your registered work. While the file as a whole may not closely match, certain individual sections, elements, or components could still contain stronger similarities that warrant closer review. A full technical report can provide a detailed breakdown of potentially significant matches and supporting technical analysis.',
  },
  [ScoreLevel.High]: {
    label: 'Significant',
    banner: 'bg-red-500',
    border: 'border-red-500',
    body: 'A significant level of similarity was identified between the compared works across one or more elements. The detected patterns may indicate a strong likelihood of infringement or unauthorized derivative use. A full technical report is recommended to review detailed evidence, comparison analysis, and supporting findings.',
  },
} as const

const copyright = computed(() => props.infringement.copyrights[0])
const infringingFile = computed(() => props.infringement.infringingFile)

const level = computed(
  () => LEVELS[props.infringement.matches?.level ?? ScoreLevel.Low]
)

const thumb = (url?: string | null) =>
  applyAuthorizationToken(url ?? undefined, [{ key: 'width', value: 72 }])

const registered = computed(() => ({
  name: copyright.value.name,
  type: fileTypeToThumbnail(copyright.value.type),
  src: thumb(copyright.value.asset?.previewImageUrl),
}))

const infringing = computed(() => ({
  name: infringingFile.value.name,
  type: fileTypeToThumbnail(infringingFile.value.type),
  src: thumb(infringingFile.value.asset?.previewImageUrl),
}))

const handleViewRegistered = () => {
  closeDialog()
  router.push(`/file/${copyright.value.copyright.id}`)
}

const handleViewInfringing = () => {
  closeDialog()
  router.push(`/file/${infringingFile.value.copyrightId}`)
}
</script>
